import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { diffLines } from 'diff';
import { Chapter, Scene, SceneHealth } from '../types';
import {
  getGraphQLClient,
  GET_BOOK_CHAPTERS_TREE,
  GENERATE_BOOK_PREVIEW,
  GENERATE_SCENE_PREVIEW,
  LOCK_SCENE,
  UNLOCK_SCENE,
  GET_SCENE_REVISIONS,
  EXPAND_BOOK,
  REORDER_CHAPTERS,
  REORDER_SCENES,
  CREATE_SCENE,
  UPDATE_CHAPTER,
  UPDATE_SCENE,
  DELETE_CHAPTER,
  DELETE_SCENE,
  GENERATE_CHAPTER_COVERS,
  downloadBookAsEpub,
} from '../lib/graphql';
import { ContextItemsPanel } from '../components/refine/ContextItemsPanel';
import BookSetupCard from '../components/refine/BookSetupCard';
import VoiceDrawer from '../components/refine/VoiceDrawer';
import GithubSyncBanner from '../components/github/GithubSyncBanner';
import RichContent from '../components/common/RichContent';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { captureException } from '../lib/sentry';
import {
  Button,
  Card,
  Spinner,
  ConfirmDialog,
  Modal,
  Drawer,
  DropdownMenu,
  DropdownMenuItem,
  Input,
} from '../components/common';

const AUTO_REGENERATE_DEBOUNCE_MS = 1500;

type ExpansionStatus = 'not_expanded' | 'expanding' | 'expansion_completed' | 'expansion_failed';

interface TextRevision {
  id: string;
  version: number;
  contentKind: string;
  origin: string;
  content: string;
  createdAt: string;
}

type ChaptersTreeResponse = {
  book: {
    id: string;
    title: string;
    fullTitle: string;
    expansionStatus?: ExpansionStatus;
    chapters: Chapter[];
  } | null;
};

const HEALTH_DOT_COLORS: Record<SceneHealth, string> = {
  thin: 'bg-red-400',
  in_progress: 'bg-amber-400',
  ready: 'bg-emerald-500',
  polished: 'bg-indigo-500',
};

function HealthDot({ health }: { health?: SceneHealth }) {
  const { t } = useTranslation();
  if (!health) return null;
  const label = t(`chaptersFirst.health.${health}`, health);
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full shrink-0 ${HEALTH_DOT_COLORS[health]}`}
      title={label}
      aria-label={label}
    />
  );
}

function formatRelativeTime(date: Date | null, locale: string | undefined): string {
  if (!date) return '';
  const diffMs = Date.now() - date.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return date.toLocaleDateString(locale);
}

export default function RefinementWorkspacePage() {
  const { t, i18n } = useTranslation();
  const { bookId } = useParams<{ bookId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const triggerInitial = searchParams.get('generate') === 'true';

  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [bookTitle, setBookTitle] = useState('');
  const [expansionStatus, setExpansionStatus] = useState<ExpansionStatus>('not_expanded');
  const [isLoadingTree, setIsLoadingTree] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSceneId, setSelectedSceneId] = useState<string | null>(null);
  const [bookPreviewGenerating, setBookPreviewGenerating] = useState(false);
  const [sceneBusy, setSceneBusy] = useState<Record<string, boolean>>({});
  const [showUnlockConfirm, setShowUnlockConfirm] = useState(false);
  const [historyForSceneId, setHistoryForSceneId] = useState<string | null>(null);
  const [autoRegenPending, setAutoRegenPending] = useState<Record<string, boolean>>({});
  const autoRegenTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const [isDownloading, setIsDownloading] = useState(false);
  const [refineSceneId, setRefineSceneId] = useState<string | null>(null);
  const [lastRegenAt, setLastRegenAt] = useState<Date | null>(null);
  const [lastRegenSceneId, setLastRegenSceneId] = useState<string | null>(null);
  const [isDeepening, setIsDeepening] = useState(false);
  const [showDeepenConfirm, setShowDeepenConfirm] = useState(false);
  const [expandedChapterIds, setExpandedChapterIds] = useState<Set<string>>(new Set());
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState<boolean>(() => {
    try {
      return window?.localStorage?.getItem('refineSidebarHidden') === 'true';
    } catch {
      return false;
    }
  });
  const [onlyNeedsWork, setOnlyNeedsWork] = useState<boolean>(false);
  // Suppress scroll-spy briefly after programmatic scrollIntoView so it doesn't
  // fight the user's sidebar click.
  const scrollSpySuppressedUntilRef = useRef<number>(0);

  // Structure-editing state (inline rename, delete confirmation, AI scene suggest)
  const [editingChapterTitleId, setEditingChapterTitleId] = useState<string | null>(null);
  const [editingSceneTitleId, setEditingSceneTitleId] = useState<string | null>(null);
  const [deleteChapterTarget, setDeleteChapterTarget] = useState<Chapter | null>(null);
  const [deleteSceneTarget, setDeleteSceneTarget] = useState<{
    scene: Scene;
    chapter: Chapter;
  } | null>(null);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [voiceDrawerOpen, setVoiceDrawerOpen] = useState<boolean>(
    () => searchParams.get('voice') === 'open'
  );

  const openVoiceDrawer = useCallback(() => {
    setVoiceDrawerOpen(true);
    setHeaderMenuOpen(false);
  }, []);

  const closeVoiceDrawer = useCallback(() => {
    setVoiceDrawerOpen(false);
    if (searchParams.get('voice')) {
      searchParams.delete('voice');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const [suggestingScenesForChapterId, setSuggestingScenesForChapterId] = useState<string | null>(
    null
  );

  const fetchTree = useCallback(async () => {
    if (!bookId) return;
    setError(null);
    try {
      const client = getGraphQLClient();
      const data = await client.request<ChaptersTreeResponse>(GET_BOOK_CHAPTERS_TREE, {
        id: bookId,
      });
      if (!data.book) {
        setError(t('common.notFound', 'Not found'));
        setChapters([]);
        return;
      }
      const sorted = [...data.book.chapters].sort((a, b) => a.position - b.position);
      setChapters(sorted);
      setBookTitle(data.book.fullTitle || data.book.title || '');
      if (data.book.expansionStatus) setExpansionStatus(data.book.expansionStatus);
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setIsLoadingTree(false);
    }
  }, [bookId, t]);

  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  // Default-select the first scene once chapters load.
  useEffect(() => {
    if (selectedSceneId) return;
    const firstSub = chapters.flatMap((c) => c.scenes ?? [])[0];
    if (firstSub) setSelectedSceneId(firstSub.id);
  }, [chapters, selectedSceneId]);

  // One-shot trigger for initial whole-book preview after applying chapter flow
  useEffect(() => {
    if (!bookId || !triggerInitial) return;
    let cancelled = false;
    const run = async () => {
      setBookPreviewGenerating(true);
      try {
        const client = getGraphQLClient();
        await client.request(GENERATE_BOOK_PREVIEW, { bookId });
        if (cancelled) return;
        await fetchTree();
      } catch (err) {
        captureException(err);
        if (!cancelled) setError(t('common.error'));
      } finally {
        if (!cancelled) {
          setBookPreviewGenerating(false);
          searchParams.delete('generate');
          setSearchParams(searchParams, { replace: true });
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId, triggerInitial]);

  const allScenes = useMemo(() => chapters.flatMap((c) => c.scenes ?? []), [chapters]);

  const refineScene = useMemo<Scene | null>(() => {
    if (!refineSceneId) return null;
    return allScenes.find((s) => s.id === refineSceneId) ?? null;
  }, [allScenes, refineSceneId]);

  const refineChapter = useMemo<Chapter | null>(() => {
    if (!refineSceneId) return null;
    return chapters.find((c) => (c.scenes ?? []).some((s) => s.id === refineSceneId)) ?? null;
  }, [chapters, refineSceneId]);

  // Progress stats for the activity strip
  const stats = useMemo(() => {
    const totalSubs = allScenes.length;
    const draftedSubs = allScenes.filter((s) => !!s.previewContent).length;
    const lockedSubs = allScenes.filter((s) => s.locked).length;
    const staleSubs = allScenes.filter((s) => s.previewStale).length;
    const totalWords = allScenes.reduce((sum, s) => sum + (s.wordCount ?? 0), 0);
    return { totalSubs, draftedSubs, lockedSubs, staleSubs, totalWords };
  }, [allScenes]);

  const firstUndraftedSceneId = useMemo(
    () => allScenes.find((s) => !s.previewContent)?.id ?? null,
    [allScenes]
  );

  const activeChapterId = useMemo<string | null>(() => {
    if (!selectedSceneId) return null;
    return chapters.find((c) => (c.scenes ?? []).some((s) => s.id === selectedSceneId))?.id ?? null;
  }, [chapters, selectedSceneId]);

  // Keep the chapter containing the active scene expanded automatically
  useEffect(() => {
    if (!activeChapterId) return;
    setExpandedChapterIds((prev) => {
      if (prev.has(activeChapterId)) return prev;
      const next = new Set(prev);
      next.add(activeChapterId);
      return next;
    });
  }, [activeChapterId]);

  const toggleChapterExpanded = useCallback((chapterId: string) => {
    setExpandedChapterIds((prev) => {
      const next = new Set(prev);
      if (next.has(chapterId)) next.delete(chapterId);
      else next.add(chapterId);
      return next;
    });
  }, []);

  const focusScene = useCallback((sceneId: string) => {
    setSelectedSceneId(sceneId);
    const el = document.getElementById(`scene-${sceneId}`);
    if (el) {
      scrollSpySuppressedUntilRef.current = Date.now() + 800;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const focusChapter = useCallback((chapterId: string) => {
    const el = document.getElementById(`chapter-${chapterId}`);
    if (el) {
      scrollSpySuppressedUntilRef.current = Date.now() + 800;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarHidden((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem('refineSidebarHidden', String(next));
      } catch {
        // ignore storage errors (Safari private mode, etc.)
      }
      return next;
    });
  }, []);

  const reorderChapters = useCallback(
    async (orderedIds: string[]) => {
      if (!bookId) return;
      const previous = chapters;
      const map = new Map(previous.map((c) => [c.id, c]));
      setChapters(orderedIds.map((id, i) => ({ ...(map.get(id) as Chapter), position: i })));
      try {
        const client = getGraphQLClient();
        await client.request(REORDER_CHAPTERS, { bookId, chapterIds: orderedIds });
        await fetchTree();
      } catch (err) {
        captureException(err);
        setChapters(previous);
        setError(t('common.error'));
      }
    },
    [bookId, chapters, fetchTree, t]
  );

  const reorderScenes = useCallback(
    async (chapterId: string, orderedIds: string[]) => {
      const previous = chapters;
      setChapters((prev) =>
        prev.map((c) => {
          if (c.id !== chapterId) return c;
          const map = new Map((c.scenes ?? []).map((s) => [s.id, s]));
          const next = orderedIds
            .map((id, i) => {
              const sub = map.get(id);
              return sub ? { ...sub, position: i } : null;
            })
            .filter(Boolean) as Scene[];
          return { ...c, scenes: next };
        })
      );
      try {
        const client = getGraphQLClient();
        await client.request(REORDER_SCENES, { chapterId, sceneIds: orderedIds });
        await fetchTree();
      } catch (err) {
        captureException(err);
        setChapters(previous);
        setError(t('common.error'));
      }
    },
    [chapters, fetchTree, t]
  );

  const addScene = useCallback(
    async (chapterId: string, position?: number) => {
      const placeholderTitle = t('chaptersFirst.newScenePlaceholder', 'Untitled scene');
      try {
        const client = getGraphQLClient();
        const res = await client.request<{
          createScene: {
            success: boolean;
            errors: string[];
            scene: { id: string } | null;
          };
        }>(CREATE_SCENE, { chapterId, title: placeholderTitle, position });
        const newId = res.createScene?.scene?.id ?? null;
        await fetchTree();
        if (newId) setEditingSceneTitleId(newId);
      } catch (err) {
        captureException(err);
        setError(t('common.error'));
      }
    },
    [fetchTree, t]
  );

  const renameChapter = useCallback(
    async (chapterId: string, title: string) => {
      const trimmed = title.trim();
      if (!trimmed) return;
      try {
        const client = getGraphQLClient();
        await client.request(UPDATE_CHAPTER, { chapterId, title: trimmed });
        setChapters((prev) => prev.map((c) => (c.id === chapterId ? { ...c, title: trimmed } : c)));
      } catch (err) {
        captureException(err);
        setError(t('common.error'));
      }
    },
    [t]
  );

  const renameScene = useCallback(
    async (sceneId: string, title: string) => {
      const trimmed = title.trim();
      if (!trimmed) return;
      try {
        const client = getGraphQLClient();
        await client.request(UPDATE_SCENE, { sceneId, title: trimmed });
        setChapters((prev) =>
          prev.map((c) => ({
            ...c,
            scenes: (c.scenes ?? []).map((s) => (s.id === sceneId ? { ...s, title: trimmed } : s)),
          }))
        );
      } catch (err) {
        captureException(err);
        setError(t('common.error'));
      }
    },
    [t]
  );

  const performDeleteChapter = useCallback(
    async (chapter: Chapter) => {
      try {
        const client = getGraphQLClient();
        await client.request(DELETE_CHAPTER, { chapterId: chapter.id });
        await fetchTree();
      } catch (err) {
        captureException(err);
        setError(t('common.error'));
      } finally {
        setDeleteChapterTarget(null);
      }
    },
    [fetchTree, t]
  );

  const performDeleteScene = useCallback(
    async (scene: Scene) => {
      try {
        const client = getGraphQLClient();
        await client.request(DELETE_SCENE, { sceneId: scene.id });
        await fetchTree();
      } catch (err) {
        captureException(err);
        setError(t('common.error'));
      } finally {
        setDeleteSceneTarget(null);
      }
    },
    [fetchTree, t]
  );

  const moveChapter = useCallback(
    (idx: number, dir: -1 | 1) => {
      const target = idx + dir;
      if (target < 0 || target >= chapters.length) return;
      const ids = chapters.map((c) => c.id);
      reorderChapters(arrayMove(ids, idx, target));
    },
    [chapters, reorderChapters]
  );

  const moveScene = useCallback(
    (chapterId: string, sceneIdx: number, dir: -1 | 1) => {
      const chapter = chapters.find((c) => c.id === chapterId);
      if (!chapter) return;
      const sceneIds = (chapter.scenes ?? []).map((s) => s.id);
      const target = sceneIdx + dir;
      if (target < 0 || target >= sceneIds.length) return;
      reorderScenes(chapterId, arrayMove(sceneIds, sceneIdx, target));
    },
    [chapters, reorderScenes]
  );

  const suggestScenesForChapter = useCallback(
    async (chapter: Chapter) => {
      if (!bookId) return;
      setSuggestingScenesForChapterId(chapter.id);
      try {
        const client = getGraphQLClient();
        const siblings = chapters
          .filter((c) => c.id !== chapter.id)
          .map((c) => c.title.trim())
          .filter((s) => s.length > 0);
        const data = await client.request<{
          generateChapterCovers: {
            success: boolean;
            errors: string[];
            covers: string[] | null;
          };
        }>(GENERATE_CHAPTER_COVERS, {
          bookId,
          chapterTitle: chapter.title,
          instructions: null,
          siblings,
        });
        const result = data.generateChapterCovers;
        if (!result.success || !result.covers) {
          setError(result.errors.join(', ') || t('common.error'));
          return;
        }
        const startPosition = (chapter.scenes ?? []).length;
        await Promise.all(
          result.covers
            .map((c) => c.trim())
            .filter((c) => c.length > 0)
            .map((text, i) =>
              client.request(CREATE_SCENE, {
                chapterId: chapter.id,
                title: text,
                position: startPosition + i,
              })
            )
        );
        await fetchTree();
      } catch (err) {
        captureException(err);
        setError(t('common.error'));
      } finally {
        setSuggestingScenesForChapterId(null);
      }
    },
    [bookId, chapters, fetchTree, t]
  );

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  // Scroll-spy: pick the scene whose top is closest to the viewport top
  // (just below the activity strip) and mark it active.
  useEffect(() => {
    if (allScenes.length === 0) return;
    const ids = allScenes.map((s) => s.id);

    const updateActive = () => {
      if (Date.now() < scrollSpySuppressedUntilRef.current) return;
      const anchor = 120; // px from top of viewport
      let best: string | null = null;
      let bestDistance = Infinity;
      for (const id of ids) {
        const el = document.getElementById(`scene-${id}`);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        // Pick the scene whose top is closest to (but not far past) anchor.
        const distance = Math.abs(rect.top - anchor);
        // Only consider sections that have started scrolling into view.
        if (rect.top - anchor <= 0 && distance < bestDistance) {
          bestDistance = distance;
          best = id;
        }
      }
      // Fallback: nothing has crossed the anchor yet — pick the first visible.
      if (!best) {
        for (const id of ids) {
          const el = document.getElementById(`scene-${id}`);
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          if (rect.bottom > 0) {
            best = id;
            break;
          }
        }
      }
      if (best) {
        setSelectedSceneId((prev) => (prev === best ? prev : best));
      }
    };

    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(updateActive);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    updateActive();
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [allScenes]);

  const setBusy = (id: string, busy: boolean) => setSceneBusy((prev) => ({ ...prev, [id]: busy }));

  const regeneratePreview = async (sceneId: string, force = false) => {
    setBusy(sceneId, true);
    setError(null);
    try {
      const client = getGraphQLClient();
      const data = await client.request<{
        generateScenePreview: {
          success: boolean;
          errors: string[];
          scene: {
            id: string;
            previewContent?: string;
            locked: boolean;
            previewStale: boolean;
          } | null;
        };
      }>(GENERATE_SCENE_PREVIEW, { sceneId, force });
      const res = data.generateScenePreview;
      if (!res.success) {
        setError(res.errors.join(', ') || t('common.error'));
        return;
      }
      setLastRegenAt(new Date());
      setLastRegenSceneId(sceneId);
      await fetchTree();
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setBusy(sceneId, false);
    }
  };

  const scheduleAutoRegenerate = useCallback(
    (sceneId: string) => {
      const existing = autoRegenTimers.current[sceneId];
      if (existing) clearTimeout(existing);

      setAutoRegenPending((prev) => ({ ...prev, [sceneId]: true }));

      autoRegenTimers.current[sceneId] = setTimeout(async () => {
        delete autoRegenTimers.current[sceneId];
        const current = chapters.flatMap((c) => c.scenes ?? []).find((s) => s.id === sceneId);
        // Skip if the scene became locked while waiting.
        if (current?.locked) {
          setAutoRegenPending((prev) => ({ ...prev, [sceneId]: false }));
          return;
        }
        try {
          await regeneratePreview(sceneId);
        } finally {
          setAutoRegenPending((prev) => ({ ...prev, [sceneId]: false }));
        }
      }, AUTO_REGENERATE_DEBOUNCE_MS);
    },
    // regeneratePreview is stable enough in this scope; chapters re-read at fire time
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chapters]
  );

  useEffect(() => {
    return () => {
      Object.values(autoRegenTimers.current).forEach((t) => clearTimeout(t));
      autoRegenTimers.current = {};
    };
  }, []);

  const handleContextChanged = useCallback(
    async (scene: Scene) => {
      await fetchTree();
      if (!scene.locked) {
        scheduleAutoRegenerate(scene.id);
      }
    },
    [fetchTree, scheduleAutoRegenerate]
  );

  const handleDownloadEpub = async () => {
    if (!bookId) return;
    setIsDownloading(true);
    try {
      await downloadBookAsEpub(bookId, bookTitle || 'book');
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setIsDownloading(false);
    }
  };

  const toggleLock = async (sub: Scene) => {
    setBusy(sub.id, true);
    setError(null);
    try {
      const client = getGraphQLClient();
      const mutation = sub.locked ? UNLOCK_SCENE : LOCK_SCENE;
      await client.request(mutation, { sceneId: sub.id });
      await fetchTree();
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setBusy(sub.id, false);
    }
  };

  const handleDeepenDraft = async () => {
    if (!bookId) return;
    setIsDeepening(true);
    setError(null);
    try {
      const client = getGraphQLClient();
      const data = await client.request<{
        expandBook: { success: boolean; errors: string[] };
      }>(EXPAND_BOOK, { bookId });
      if (!data.expandBook.success) {
        setError(data.expandBook.errors.join(', ') || t('common.error'));
        return;
      }
      setExpansionStatus('expanding');
      await fetchTree();
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setIsDeepening(false);
      setShowDeepenConfirm(false);
    }
  };

  const openRefineDrawer = (sceneId: string) => {
    setSelectedSceneId(sceneId);
    setRefineSceneId(sceneId);
  };

  if (isLoadingTree) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner />
      </div>
    );
  }

  if (error && chapters.length === 0) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card>
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={fetchTree}>{t('common.retry', 'Retry')}</Button>
        </Card>
      </div>
    );
  }

  // Primary CTA state
  const allDrafted = stats.totalSubs > 0 && stats.draftedSubs === stats.totalSubs;
  const isExpanded = expansionStatus === 'expansion_completed';
  const isExpanding = expansionStatus === 'expanding';

  return (
    <div className="max-w-7xl mx-auto px-4 py-4 md:py-6">
      {bookId && (
        <div className="mb-3">
          <GithubSyncBanner bookId={bookId} />
        </div>
      )}
      <header className="mb-3 flex flex-col md:flex-row md:items-end md:justify-between gap-2">
        <div className="min-w-0 flex-1">
          <nav
            aria-label="Breadcrumb"
            className="text-sm text-gray-500 dark:text-gray-400 mb-1 flex items-center flex-wrap gap-x-1.5"
          >
            <button
              type="button"
              onClick={() => navigate('/books')}
              className="hover:text-gray-700 dark:hover:text-gray-200 hover:underline"
            >
              {t('chaptersFirst.backToBooks', 'My books')}
            </button>
            <span className="text-gray-400 dark:text-gray-600" aria-hidden="true">
              ›
            </span>
            <button
              type="button"
              onClick={() => navigate(`/books/${bookId}/chapters-first/select`)}
              className="hover:text-gray-700 dark:hover:text-gray-200 hover:underline"
            >
              {t('chaptersFirst.chapterPlan', 'Chapter plan')}
            </button>
            <span className="text-gray-400 dark:text-gray-600" aria-hidden="true">
              ›
            </span>
            <span className="text-gray-700 dark:text-gray-300" aria-current="page">
              {t('chaptersFirst.refine', 'Refine')}
            </span>
          </nav>
          <h1 className="text-2xl font-bold truncate">{bookTitle}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Primary CTA: only "continue drafting" while there's undrafted work. */}
          {firstUndraftedSceneId && !bookPreviewGenerating && (
            <Button variant="primary" size="sm" onClick={() => focusScene(firstUndraftedSceneId)}>
              {t('chaptersFirst.continueDrafting', 'Continue drafting')}
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={() => navigate(`/books/${bookId}/read`)}>
            📖 {t('bookReader.read', 'Read')}
          </Button>
          {isExpanding && (
            <span className="text-sm text-indigo-700 dark:text-indigo-300 flex items-center gap-1">
              <Spinner />
              {t('chaptersFirst.deepeningInProgress', 'Generating manuscript...')}
            </span>
          )}
          <div className="relative">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setHeaderMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={headerMenuOpen}
              aria-label={t('chaptersFirst.bookActionsMenu', 'Book actions')}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 5v.01M12 12v.01M12 19v.01"
                />
              </svg>
            </Button>
            <DropdownMenu
              isOpen={headerMenuOpen}
              onClose={() => setHeaderMenuOpen(false)}
              position="right"
              width="md"
            >
              {/* Setup */}
              <DropdownMenuItem onClick={openVoiceDrawer}>
                {t('chaptersFirst.menuVoice', 'Voice & writing style')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setHeaderMenuOpen(false);
                  navigate(`/books/${bookId}/edit`);
                }}
              >
                {t('chaptersFirst.menuBookDetails', 'Book details')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setHeaderMenuOpen(false);
                  navigate(`/books/${bookId}/cover-editor`);
                }}
              >
                {t('chaptersFirst.menuCover', 'Cover & front matter')}
              </DropdownMenuItem>

              {/* Manuscript — only shown when applicable */}
              {allDrafted && !isExpanded && !isExpanding && !bookPreviewGenerating && (
                <>
                  <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
                  <DropdownMenuItem
                    onClick={() => {
                      setHeaderMenuOpen(false);
                      setShowDeepenConfirm(true);
                    }}
                    disabled={isDeepening}
                  >
                    {isDeepening
                      ? t('chaptersFirst.deepening', 'Generating...')
                      : t('chaptersFirst.deepenDraft', 'Generate full manuscript')}
                  </DropdownMenuItem>
                </>
              )}

              {/* Export & backup */}
              <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
              <DropdownMenuItem
                onClick={() => {
                  setHeaderMenuOpen(false);
                  handleDownloadEpub();
                }}
                disabled={isDownloading}
              >
                {isDownloading
                  ? t('common.downloading', 'Downloading...')
                  : t('chaptersFirst.menuExportEpub', 'Export EPUB')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setHeaderMenuOpen(false);
                  navigate(`/books/${bookId}/sync`);
                }}
              >
                {t('chaptersFirst.menuGithubSync', 'GitHub sync')}
              </DropdownMenuItem>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {bookId && <BookSetupCard bookId={bookId} onOpenVoice={openVoiceDrawer} />}
      {bookId && (
        <VoiceDrawer bookId={bookId} isOpen={voiceDrawerOpen} onClose={closeVoiceDrawer} />
      )}

      {/* Activity strip */}
      <div className="mb-4 flex flex-wrap items-center gap-3 text-sm text-gray-600 dark:text-gray-300 border-y border-gray-200 dark:border-gray-700 py-2">
        <span className="font-medium">
          {t('chaptersFirst.progress', 'Progress')}: {stats.draftedSubs}/{stats.totalSubs}{' '}
          {t('chaptersFirst.scenesLowercase', 'scenes')}
        </span>
        <span className="text-gray-400">·</span>
        <span>
          ~{stats.totalWords.toLocaleString()} {t('chaptersFirst.words', 'words')}
        </span>
        {stats.lockedSubs > 0 && (
          <>
            <span className="text-gray-400">·</span>
            <span>
              🔒 {stats.lockedSubs} {t('chaptersFirst.locked', 'Locked')}
            </span>
          </>
        )}
        {stats.staleSubs > 0 && (
          <>
            <span className="text-gray-400">·</span>
            <span className="text-amber-700 dark:text-amber-300">
              ⚠ {stats.staleSubs} {t('chaptersFirst.stale', 'Out of date')}
            </span>
          </>
        )}
        {bookPreviewGenerating && (
          <>
            <span className="text-gray-400">·</span>
            <span className="text-indigo-700 dark:text-indigo-300 flex items-center gap-1">
              <Spinner />
              {t('chaptersFirst.generatingBookPreview', 'Drafting the whole book...')}
            </span>
          </>
        )}
        {lastRegenAt && lastRegenSceneId && !bookPreviewGenerating && (
          <>
            <span className="text-gray-400 ml-auto">·</span>
            <button
              type="button"
              onClick={() => setHistoryForSceneId(lastRegenSceneId)}
              className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-200 underline"
            >
              {t('chaptersFirst.whatChanged', 'See what changed')} ·{' '}
              {formatRelativeTime(lastRegenAt, i18n.language)}
            </button>
          </>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Mobile-only "Chapters" button */}
      <div className="lg:hidden mb-3 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setMobileTocOpen(true)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm min-h-[44px]"
        >
          ☰ {t('planManager.title', 'Plan')}
          <span className="text-xs text-gray-400">({chapters.length})</span>
        </button>
        <button
          type="button"
          onClick={() => navigate(`/books/${bookId}/plan`)}
          className="text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-300 px-3 py-2 min-h-[44px]"
        >
          ⚙ {t('planManager.openButton', 'Manage chapters & plan')}
        </button>
      </div>

      {(() => {
        const tocBody = (
          <>
            <button
              type="button"
              onClick={() => navigate(`/books/${bookId}/plan`)}
              className="w-full mb-3 text-sm text-indigo-600 hover:text-white hover:bg-indigo-600 dark:text-indigo-300 border border-indigo-300 dark:border-indigo-700 rounded-md py-1.5 transition-colors"
            >
              ⚙ {t('planManager.openButton', 'Manage chapters & plan')}
            </button>
            <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 mb-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={onlyNeedsWork}
                onChange={(e) => setOnlyNeedsWork(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-gray-300"
              />
              {t('chaptersFirst.onlyNeedsWork', 'Only scenes needing detail')}
            </label>
            {chapters.length === 0 && (
              <p className="text-sm text-gray-500">
                {t('chaptersFirst.noChapters', 'No chapters yet.')}
              </p>
            )}
            <DndContext
              sensors={dndSensors}
              collisionDetection={closestCenter}
              onDragEnd={(event: DragEndEvent) => {
                const { active, over } = event;
                if (!over || active.id === over.id) return;
                const ids = chapters.map((c) => c.id);
                const oldIndex = ids.indexOf(String(active.id));
                const newIndex = ids.indexOf(String(over.id));
                if (oldIndex === -1 || newIndex === -1) return;
                reorderChapters(arrayMove(ids, oldIndex, newIndex));
              }}
            >
              <SortableContext
                items={chapters.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                <ol className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
                  {chapters.map((chapter, ci) => (
                    <SortableChapterItem
                      key={chapter.id}
                      chapter={chapter}
                      index={ci}
                      isExpanded={expandedChapterIds.has(chapter.id)}
                      selectedSceneId={selectedSceneId}
                      onlyNeedsWork={onlyNeedsWork}
                      bookId={bookId}
                      onToggleExpanded={toggleChapterExpanded}
                      onFocusChapter={(id) => {
                        focusChapter(id);
                        setMobileTocOpen(false);
                      }}
                      onFocusScene={(id) => {
                        focusScene(id);
                        setMobileTocOpen(false);
                      }}
                      onReorderScenes={reorderScenes}
                      onAddScene={addScene}
                      onNavigate={(path) => {
                        navigate(path);
                        setMobileTocOpen(false);
                      }}
                      sensors={dndSensors}
                      onMoveUp={() => moveChapter(ci, -1)}
                      onMoveDown={() => moveChapter(ci, 1)}
                      canMoveUp={ci > 0}
                      canMoveDown={ci < chapters.length - 1}
                      onMoveScene={moveScene}
                    />
                  ))}
                </ol>
              </SortableContext>
            </DndContext>
          </>
        );

        return (
          <>
            <Drawer
              isOpen={mobileTocOpen}
              onClose={() => setMobileTocOpen(false)}
              title={t('chaptersFirst.chapters', 'Chapters')}
              width="md"
            >
              <div className="px-4 py-3 overflow-y-auto">{tocBody}</div>
            </Drawer>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Desktop-only sidebar */}
              {!sidebarHidden && (
                <aside className="hidden lg:block lg:col-span-3">
                  <Card padding="md" className="lg:sticky lg:top-4">
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {t('planManager.title', 'Plan')}
                      </h2>
                      <button
                        type="button"
                        onClick={toggleSidebar}
                        aria-label={t('chaptersFirst.hideToc', 'Hide table of contents')}
                        className="p-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                        title={t('chaptersFirst.hideToc', 'Hide table of contents')}
                      >
                        «
                      </button>
                    </div>
                    {tocBody}
                  </Card>
                </aside>
              )}

              {/* Right panel: Continuous book */}
              <main className={`${sidebarHidden ? 'lg:col-span-12' : 'lg:col-span-9'} space-y-10`}>
                {sidebarHidden && (
                  <button
                    type="button"
                    onClick={toggleSidebar}
                    aria-label={t('chaptersFirst.showToc', 'Show table of contents')}
                    title={t('chaptersFirst.showToc', 'Show table of contents')}
                    className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 px-2 py-1 rounded border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    » {t('chaptersFirst.showToc', 'Show table of contents')}
                  </button>
                )}

                {chapters.length === 0 && (
                  <Card>
                    <p className="text-gray-500">
                      {t('chaptersFirst.noChapters', 'No chapters yet.')}
                    </p>
                  </Card>
                )}

                {chapters.map((chapter, ci) => (
                  <section key={chapter.id} id={`chapter-${chapter.id}`} className="scroll-mt-4">
                    <header className="mb-4 flex items-start justify-between gap-3 border-b border-gray-200 dark:border-gray-700 pb-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs uppercase tracking-wide text-gray-500">
                          {t('chaptersFirst.chapter', 'Chapter')} {ci + 1}
                        </div>
                        <ChapterTitleEditable
                          chapter={chapter}
                          isEditing={editingChapterTitleId === chapter.id}
                          onStartEditing={() => setEditingChapterTitleId(chapter.id)}
                          onStopEditing={(nextTitle) => {
                            setEditingChapterTitleId(null);
                            if (nextTitle && nextTitle !== chapter.title) {
                              void renameChapter(chapter.id, nextTitle);
                            }
                          }}
                        />
                        {chapter.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {chapter.description}
                          </p>
                        )}
                      </div>
                      <ChapterActionsMenu
                        chapter={chapter}
                        index={ci}
                        total={chapters.length}
                        isSuggesting={suggestingScenesForChapterId === chapter.id}
                        onRename={() => setEditingChapterTitleId(chapter.id)}
                        onAddSceneEnd={() => addScene(chapter.id, (chapter.scenes ?? []).length)}
                        onMoveUp={() => moveChapter(ci, -1)}
                        onMoveDown={() => moveChapter(ci, 1)}
                        onSuggestScenes={() => suggestScenesForChapter(chapter)}
                        onReshape={() =>
                          navigate(`/books/${bookId}/chapters/${chapter.id}/scenes-first/select`)
                        }
                        onDelete={() => setDeleteChapterTarget(chapter)}
                      />
                    </header>

                    <div className="space-y-8">
                      {(chapter.scenes ?? []).map((sub, si) => (
                        <div key={sub.id}>
                          {si > 0 && (
                            <InsertSceneHairline
                              label={t('chaptersFirst.insertSceneHere', 'Insert scene here')}
                              onInsert={() => addScene(chapter.id, si)}
                            />
                          )}
                          <SceneBlock
                            scene={sub}
                            isActive={sub.id === selectedSceneId}
                            busy={!!sceneBusy[sub.id] || bookPreviewGenerating}
                            autoRegenPending={!!autoRegenPending[sub.id]}
                            isEditingTitle={editingSceneTitleId === sub.id}
                            isFirst={si === 0}
                            isLast={si === (chapter.scenes ?? []).length - 1}
                            onSelect={() => setSelectedSceneId(sub.id)}
                            onOpenEditor={() => navigate(`/books/${bookId}/scenes/${sub.id}`)}
                            onOpenRefine={() => openRefineDrawer(sub.id)}
                            onRegenerate={() => regeneratePreview(sub.id)}
                            onForceRegenerate={() => regeneratePreview(sub.id, true)}
                            onStartRenaming={() => setEditingSceneTitleId(sub.id)}
                            onStopRenaming={(nextTitle) => {
                              setEditingSceneTitleId(null);
                              if (nextTitle && nextTitle !== sub.title) {
                                void renameScene(sub.id, nextTitle);
                              }
                            }}
                            onMoveUp={() => moveScene(chapter.id, si, -1)}
                            onMoveDown={() => moveScene(chapter.id, si, 1)}
                            onDelete={() => setDeleteSceneTarget({ scene: sub, chapter })}
                          />
                        </div>
                      ))}
                      {(chapter.scenes ?? []).length === 0 && (
                        <div className="text-center py-6 space-y-2">
                          <p className="text-sm text-gray-500 italic">
                            {t('chaptersFirst.noScenesYet', 'No scenes yet for this chapter.')}
                          </p>
                          <Button variant="ghost" size="sm" onClick={() => addScene(chapter.id, 0)}>
                            + {t('chaptersFirst.addScene', 'Add scene')}
                          </Button>
                        </div>
                      )}
                      {(chapter.scenes ?? []).length > 0 && (
                        <InsertSceneHairline
                          label={t('chaptersFirst.addSceneBelow', 'Add scene at the end')}
                          onInsert={() => addScene(chapter.id, (chapter.scenes ?? []).length)}
                        />
                      )}
                    </div>
                  </section>
                ))}
              </main>
            </div>
          </>
        );
      })()}

      <ConfirmDialog
        isOpen={showUnlockConfirm}
        title={t('chaptersFirst.confirmUnlock', 'Unlock this scene?')}
        message={t(
          'chaptersFirst.confirmUnlockHelp',
          'After unlocking, automatic regeneration may overwrite your edits when context changes.'
        )}
        confirmText={t('chaptersFirst.unlock', 'Unlock')}
        cancelText={t('common.cancel', 'Cancel')}
        onConfirm={() => {
          setShowUnlockConfirm(false);
          if (refineScene) toggleLock(refineScene);
        }}
        onClose={() => setShowUnlockConfirm(false)}
      />

      <ConfirmDialog
        isOpen={showDeepenConfirm}
        title={t('chaptersFirst.deepenDraft', 'Generate full manuscript')}
        message={t(
          'chaptersFirst.deepenDraftHelp',
          'This expands every compressed scene draft into full prose (~60–90k words total). It replaces the current scene drafts and takes several minutes.'
        )}
        confirmText={t('chaptersFirst.deepenConfirm', 'Generate manuscript')}
        cancelText={t('common.cancel', 'Cancel')}
        onConfirm={handleDeepenDraft}
        onClose={() => setShowDeepenConfirm(false)}
      />

      <ConfirmDialog
        isOpen={!!deleteChapterTarget}
        confirmVariant="danger"
        title={t('chaptersFirst.confirmDeleteChapterTitle', 'Delete this chapter?')}
        message={
          deleteChapterTarget
            ? t('chaptersFirst.confirmDeleteChapterBody', {
                defaultValue:
                  'Delete "{{title}}" and its {{count}} scene(s) ({{words}} words)? This cannot be undone.',
                title: deleteChapterTarget.title,
                count: (deleteChapterTarget.scenes ?? []).length,
                words: (deleteChapterTarget.scenes ?? []).reduce(
                  (sum, s) => sum + (s.wordCount ?? 0),
                  0
                ),
              })
            : ''
        }
        confirmText={t('chaptersFirst.confirmDeleteChapterAction', 'Delete chapter')}
        cancelText={t('common.cancel', 'Cancel')}
        onConfirm={() => deleteChapterTarget && performDeleteChapter(deleteChapterTarget)}
        onClose={() => setDeleteChapterTarget(null)}
      />

      <ConfirmDialog
        isOpen={!!deleteSceneTarget}
        confirmVariant="danger"
        title={t('chaptersFirst.confirmDeleteSceneTitle', 'Delete this scene?')}
        message={
          deleteSceneTarget
            ? t('chaptersFirst.confirmDeleteSceneBody', {
                defaultValue:
                  'Delete "{{title}}" ({{words}} words) from "{{chapter}}"? This cannot be undone.',
                title: deleteSceneTarget.scene.title,
                words: deleteSceneTarget.scene.wordCount ?? 0,
                chapter: deleteSceneTarget.chapter.title,
              })
            : ''
        }
        confirmText={t('chaptersFirst.confirmDeleteSceneAction', 'Delete scene')}
        cancelText={t('common.cancel', 'Cancel')}
        onConfirm={() => deleteSceneTarget && performDeleteScene(deleteSceneTarget.scene)}
        onClose={() => setDeleteSceneTarget(null)}
      />

      {historyForSceneId && (
        <HistoryModal sceneId={historyForSceneId} onClose={() => setHistoryForSceneId(null)} />
      )}

      <Drawer
        isOpen={!!refineScene}
        onClose={() => setRefineSceneId(null)}
        title={refineScene?.title}
        subtitle={refineChapter?.title}
        width="md"
      >
        {refineScene && (
          <RefineDrawerContent
            scene={refineScene}
            busy={!!sceneBusy[refineScene.id] || bookPreviewGenerating}
            autoRegenPending={!!autoRegenPending[refineScene.id]}
            onOpenEditor={() => navigate(`/books/${bookId}/scenes/${refineScene.id}`)}
            onRegenerate={() => regeneratePreview(refineScene.id)}
            onForceRegenerate={() => regeneratePreview(refineScene.id, true)}
            onToggleLock={() => {
              if (refineScene.locked) {
                setShowUnlockConfirm(true);
              } else {
                toggleLock(refineScene);
              }
            }}
            onShowHistory={() => setHistoryForSceneId(refineScene.id)}
            onContextChanged={() => handleContextChanged(refineScene)}
            onError={(msg) => setError(msg)}
          />
        )}
      </Drawer>
    </div>
  );
}

interface SceneBlockProps {
  scene: Scene;
  isActive: boolean;
  busy: boolean;
  autoRegenPending: boolean;
  isEditingTitle: boolean;
  isFirst: boolean;
  isLast: boolean;
  onSelect: () => void;
  onOpenEditor: () => void;
  onOpenRefine: () => void;
  onRegenerate: () => void;
  onForceRegenerate: () => void;
  onStartRenaming: () => void;
  onStopRenaming: (nextTitle: string | null) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}

function SceneBlock({
  scene,
  isActive,
  busy,
  autoRegenPending,
  isEditingTitle,
  isFirst,
  isLast,
  onSelect,
  onOpenEditor,
  onOpenRefine,
  onRegenerate,
  onForceRegenerate,
  onStartRenaming,
  onStopRenaming,
  onMoveUp,
  onMoveDown,
  onDelete,
}: SceneBlockProps) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <article
      id={`scene-${scene.id}`}
      onClick={() => {
        if (!isActive) onSelect();
      }}
      className={`scroll-mt-4 rounded-lg transition-all ${
        isActive
          ? 'ring-2 ring-indigo-300 dark:ring-indigo-700 bg-white dark:bg-gray-900 p-4 -mx-4 shadow-sm'
          : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40 p-4 -mx-4'
      }`}
    >
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          {isEditingTitle ? (
            <TitleEditInput initialValue={scene.title} onCommit={onStopRenaming} />
          ) : (
            <h3
              className="text-xl font-bold cursor-text rounded-md -mx-1 px-1 hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onStartRenaming();
              }}
              title={t('chaptersFirst.renameSceneHint', 'Click to rename')}
            >
              {scene.title}
            </h3>
          )}
          {scene.body && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
              {scene.body.slice(0, 200)}
            </p>
          )}
          <div className="flex flex-wrap gap-2 mt-2 text-xs">
            {scene.locked && (
              <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                🔒 {t('chaptersFirst.locked', 'Locked')}
              </span>
            )}
            {scene.previewStale && (
              <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200">
                ⚠ {t('chaptersFirst.stale', 'Out of date')}
              </span>
            )}
            {autoRegenPending && !busy && (
              <span className="px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 flex items-center gap-1">
                <Spinner />
                {t('chaptersFirst.autoRegenerating', 'Auto-regenerating soon...')}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center shrink-0">
          <Button
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onOpenEditor();
            }}
          >
            {t('chaptersFirst.editSceneShort', 'Edit')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onOpenRefine();
            }}
          >
            {t('chaptersFirst.refine', 'Refine')}
          </Button>
          <div className="relative">
            <button
              type="button"
              aria-label={t('common.options', 'Options')}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((o) => !o);
              }}
              className="inline-flex items-center justify-center w-8 h-8 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                <circle cx="10" cy="4" r="1.6" />
                <circle cx="10" cy="10" r="1.6" />
                <circle cx="10" cy="16" r="1.6" />
              </svg>
            </button>
            <DropdownMenu
              isOpen={menuOpen}
              onClose={() => setMenuOpen(false)}
              position="right"
              width="md"
            >
              <DropdownMenuItem
                onClick={() => {
                  setMenuOpen(false);
                  onStartRenaming();
                }}
              >
                {t('common.rename', 'Rename')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setMenuOpen(false);
                  onMoveUp();
                }}
                disabled={isFirst}
              >
                {t('common.moveUp', 'Move up')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setMenuOpen(false);
                  onMoveDown();
                }}
                disabled={isLast}
              >
                {t('common.moveDown', 'Move down')}
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="danger"
                onClick={() => {
                  setMenuOpen(false);
                  onDelete();
                }}
              >
                {t('common.delete', 'Delete')}
              </DropdownMenuItem>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {scene.previewStale && !scene.locked && (
        <div className="mb-3 p-2 text-sm bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-amber-800 dark:text-amber-200 flex items-center justify-between gap-2 flex-wrap">
          <span>
            ⚠️ {t('chaptersFirst.staleWarning', 'Context changed — the preview is out of date.')}
          </span>
          <Button
            variant="primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onRegenerate();
            }}
            disabled={busy}
          >
            {t('chaptersFirst.regeneratePreview', 'Regenerate preview')}
          </Button>
        </div>
      )}
      {scene.previewStale && scene.locked && (
        <div className="mb-3 p-2 text-sm bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-amber-800 dark:text-amber-200 flex items-center justify-between gap-2 flex-wrap">
          <span>
            🔒{' '}
            {t(
              'chaptersFirst.staleLockedWarning',
              'This scene is locked but the context has changed. Unlock or force-regenerate to refresh.'
            )}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onForceRegenerate();
            }}
            disabled={busy}
          >
            {t('chaptersFirst.regenerateForce', 'Regenerate anyway')}
          </Button>
        </div>
      )}

      {busy ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
          <Spinner />
          {t('chaptersFirst.generating', 'Generating...')}
        </div>
      ) : scene.body ? (
        <RichContent content={scene.body} />
      ) : (
        <p className="text-sm text-gray-500 py-6 text-center">
          {t('chaptersFirst.noContentYet', 'No content yet')}
        </p>
      )}
    </article>
  );
}

interface RefineDrawerContentProps {
  scene: Scene;
  busy: boolean;
  autoRegenPending: boolean;
  onOpenEditor: () => void;
  onRegenerate: () => void;
  onForceRegenerate: () => void;
  onToggleLock: () => void;
  onShowHistory: () => void;
  onContextChanged: () => Promise<void> | void;
  onError: (msg: string) => void;
}

function RefineDrawerContent({
  scene,
  busy,
  autoRegenPending,
  onOpenEditor,
  onRegenerate,
  onForceRegenerate,
  onToggleLock,
  onShowHistory,
  onContextChanged,
  onError,
}: RefineDrawerContentProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-5">
      {scene.body && (
        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
          {scene.body.slice(0, 240)}
        </p>
      )}

      <div className="flex flex-wrap gap-2 text-xs">
        {scene.locked && (
          <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
            🔒 {t('chaptersFirst.locked', 'Locked')}
          </span>
        )}
        {scene.previewStale && (
          <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200">
            ⚠ {t('chaptersFirst.stale', 'Out of date')}
          </span>
        )}
        {autoRegenPending && !busy && (
          <span className="px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 flex items-center gap-1">
            <Spinner />
            {t('chaptersFirst.autoRegenerating', 'Auto-regenerating soon...')}
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={onOpenEditor} disabled={busy}>
          {t('chaptersFirst.editScene', 'Edit scene')}
        </Button>
        {scene.locked ? (
          <Button variant="ghost" size="sm" onClick={onForceRegenerate} disabled={busy}>
            {t('chaptersFirst.regenerateForce', 'Regenerate anyway')}
          </Button>
        ) : (
          <Button variant="primary" size="sm" onClick={onRegenerate} disabled={busy}>
            {t('chaptersFirst.regeneratePreview', 'Regenerate preview')}
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          onClick={onToggleLock}
          disabled={busy}
          aria-label={scene.locked ? 'unlock' : 'lock'}
        >
          {scene.locked ? t('chaptersFirst.unlock', 'Unlock') : t('chaptersFirst.lock', 'Lock')}
        </Button>
        <Button variant="ghost" size="sm" onClick={onShowHistory} disabled={busy}>
          {t('chaptersFirst.history', 'History')}
        </Button>
      </div>

      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <ContextItemsPanel
          contextableType="Scene"
          contextableId={scene.id}
          items={scene.contextItems ?? []}
          onChanged={onContextChanged}
          onError={onError}
        />
      </div>
    </div>
  );
}

interface HistoryModalProps {
  sceneId: string;
  onClose: () => void;
}

function HistoryModal({ sceneId, onClose }: HistoryModalProps) {
  const { t } = useTranslation();
  const [revisions, setRevisions] = useState<TextRevision[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const client = getGraphQLClient();
        const data = await client.request<{
          scene: { textRevisions: TextRevision[] } | null;
        }>(GET_SCENE_REVISIONS, { id: sceneId });
        if (cancelled) return;
        const all = data.scene?.textRevisions ?? [];
        const previews = all
          .filter((r) => r.contentKind === 'preview')
          .sort((a, b) => b.version - a.version);
        setRevisions(previews);
        if (previews.length > 0) {
          // Default selection: the previous revision (one older than latest)
          setSelectedVersion(previews[1]?.version ?? previews[0].version);
        }
      } catch (err) {
        captureException(err);
        if (!cancelled) setLoadError(t('common.error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [sceneId, t]);

  const latest = revisions?.[0] ?? null;
  const compareAgainst =
    selectedVersion != null
      ? (revisions?.find((r) => r.version === selectedVersion) ?? null)
      : null;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('chaptersFirst.historyTitle', 'Preview history')}
      size="2xl"
    >
      {loading && (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      )}
      {!loading && loadError && <p className="text-red-600">{loadError}</p>}
      {!loading && revisions && revisions.length === 0 && (
        <p className="text-sm text-gray-500">
          {t('chaptersFirst.noRevisions', 'No saved revisions yet.')}
        </p>
      )}
      {!loading && revisions && revisions.length > 0 && latest && (
        <div className="space-y-4">
          <div className="text-xs text-gray-500">
            {t('chaptersFirst.compareHelp', 'Compare the latest preview with an older revision.')}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm">{t('chaptersFirst.compareWith', 'Compare with')}</label>
            <select
              value={selectedVersion ?? ''}
              onChange={(e) => setSelectedVersion(Number(e.target.value))}
              className="px-3 py-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
            >
              {revisions.map((r) => (
                <option key={r.id} value={r.version}>
                  v{r.version} — {new Date(r.createdAt).toLocaleString()}
                  {r.version === latest.version ? ` (${t('chaptersFirst.latest', 'latest')})` : ''}
                </option>
              ))}
            </select>
          </div>

          {compareAgainst && compareAgainst.version === latest.version ? (
            <div className="text-sm text-gray-500">
              {t(
                'chaptersFirst.compareSameAsLatest',
                'Pick an older revision to see what changed.'
              )}
            </div>
          ) : (
            compareAgainst && (
              <DiffView previous={compareAgainst.content} current={latest.content} />
            )
          )}
        </div>
      )}
    </Modal>
  );
}

interface DiffViewProps {
  previous: string;
  current: string;
}

function DiffView({ previous, current }: DiffViewProps) {
  const parts = useMemo(() => diffLines(previous, current), [previous, current]);
  return (
    <div className="font-mono text-xs whitespace-pre-wrap bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-3 max-h-[60vh] overflow-auto">
      {parts.map((p, i) => {
        if (p.added) {
          return (
            <span
              key={i}
              className="bg-green-100 dark:bg-green-900/40 text-green-900 dark:text-green-200 block"
            >
              + {p.value}
            </span>
          );
        }
        if (p.removed) {
          return (
            <span
              key={i}
              className="bg-red-100 dark:bg-red-900/40 text-red-900 dark:text-red-200 block"
            >
              − {p.value}
            </span>
          );
        }
        return (
          <span key={i} className="block text-gray-600 dark:text-gray-400">
            {'  ' + p.value}
          </span>
        );
      })}
    </div>
  );
}

interface SortableChapterItemProps {
  chapter: Chapter;
  index: number;
  isExpanded: boolean;
  selectedSceneId: string | null;
  onlyNeedsWork: boolean;
  bookId: string | undefined;
  onToggleExpanded: (id: string) => void;
  onFocusChapter: (id: string) => void;
  onFocusScene: (id: string) => void;
  onReorderScenes: (chapterId: string, ids: string[]) => void;
  onAddScene: (chapterId: string) => void;
  onNavigate: (path: string) => void;
  sensors: ReturnType<typeof useSensors>;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onMoveScene?: (chapterId: string, sceneIdx: number, dir: -1 | 1) => void;
}

function SortableChapterItem({
  chapter,
  index,
  isExpanded,
  selectedSceneId,
  onlyNeedsWork,
  bookId,
  onToggleExpanded,
  onFocusChapter,
  onFocusScene,
  onReorderScenes,
  onAddScene,
  onNavigate,
  sensors,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  onMoveScene,
}: SortableChapterItemProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: chapter.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const visibleScenes = (chapter.scenes ?? []).filter(
    (sub) => !onlyNeedsWork || sub.health === 'thin' || sub.health === 'in_progress'
  );

  return (
    <li ref={setNodeRef} style={style}>
      <div className="flex items-center gap-1 mb-1">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="hidden lg:inline-flex text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing px-1"
          aria-label={t('chaptersFirst.dragHandle', 'Drag to reorder')}
          title={t('chaptersFirst.dragHandle', 'Drag to reorder')}
        >
          ⋮⋮
        </button>
        <div className="flex flex-col lg:hidden -my-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={!canMoveUp}
            className="px-1.5 text-gray-400 disabled:opacity-30 hover:text-indigo-600 leading-none text-xs h-5"
            aria-label={t('chaptersFirst.moveUp', 'Move up')}
            title={t('chaptersFirst.moveUp', 'Move up')}
          >
            ▲
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={!canMoveDown}
            className="px-1.5 text-gray-400 disabled:opacity-30 hover:text-indigo-600 leading-none text-xs h-5"
            aria-label={t('chaptersFirst.moveDown', 'Move down')}
            title={t('chaptersFirst.moveDown', 'Move down')}
          >
            ▼
          </button>
        </div>
        <button
          type="button"
          onClick={() => onToggleExpanded(chapter.id)}
          aria-label={
            isExpanded
              ? t('chaptersFirst.collapseChapter', 'Collapse chapter')
              : t('chaptersFirst.expandChapter', 'Expand chapter')
          }
          aria-expanded={isExpanded}
          className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 text-xs shrink-0"
        >
          {isExpanded ? '▾' : '▸'}
        </button>
        <button
          type="button"
          onClick={() => onFocusChapter(chapter.id)}
          className="flex-1 min-w-0 text-left text-sm font-semibold text-gray-800 dark:text-gray-200 hover:text-indigo-700 dark:hover:text-indigo-300 truncate"
        >
          {index + 1}. {chapter.title}
        </button>
        <button
          type="button"
          onClick={() => onNavigate(`/books/${bookId}/chapters/${chapter.id}`)}
          className="text-xs text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-300 px-1"
          aria-label={t('chaptersFirst.openChapterFocus', 'Open chapter')}
          title={t('chaptersFirst.openChapterFocus', 'Open chapter')}
        >
          ↗
        </button>
      </div>
      {isExpanded && (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(event: DragEndEvent) => {
              const { active, over } = event;
              if (!over || active.id === over.id) return;
              const ids = (chapter.scenes ?? []).map((s) => s.id);
              const oldIndex = ids.indexOf(String(active.id));
              const newIndex = ids.indexOf(String(over.id));
              if (oldIndex === -1 || newIndex === -1) return;
              onReorderScenes(chapter.id, arrayMove(ids, oldIndex, newIndex));
            }}
          >
            <SortableContext
              items={visibleScenes.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="ml-5 space-y-1 border-l border-gray-200 dark:border-gray-700 pl-2">
                {visibleScenes.map((sub, si) => {
                  const sceneIdxInChapter = (chapter.scenes ?? []).findIndex(
                    (s) => s.id === sub.id
                  );
                  return (
                    <SortableSceneItem
                      key={sub.id}
                      scene={sub}
                      bookId={bookId}
                      isSelected={sub.id === selectedSceneId}
                      onFocusScene={onFocusScene}
                      onNavigate={onNavigate}
                      onMoveUp={
                        onMoveScene && sceneIdxInChapter > 0
                          ? () => onMoveScene(chapter.id, sceneIdxInChapter, -1)
                          : undefined
                      }
                      onMoveDown={
                        onMoveScene && sceneIdxInChapter < (chapter.scenes?.length ?? 0) - 1
                          ? () => onMoveScene(chapter.id, sceneIdxInChapter, 1)
                          : undefined
                      }
                      canMoveUp={si > 0}
                      canMoveDown={si < visibleScenes.length - 1}
                    />
                  );
                })}
              </ul>
            </SortableContext>
          </DndContext>
          <button
            type="button"
            onClick={() => onAddScene(chapter.id)}
            className="ml-5 mt-1 text-xs text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-300"
          >
            + {t('chaptersFirst.addScene', 'Add scene')}
          </button>
        </>
      )}
    </li>
  );
}

interface SortableSceneItemProps {
  scene: Scene;
  bookId: string | undefined;
  isSelected: boolean;
  onFocusScene: (id: string) => void;
  onNavigate: (path: string) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
}

function SortableSceneItem({
  scene,
  bookId,
  isSelected,
  onFocusScene,
  onNavigate,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: SortableSceneItemProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: scene.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li ref={setNodeRef} style={style} className="group flex items-center gap-1">
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="hidden lg:inline-flex text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing px-0.5 text-xs"
        aria-label={t('chaptersFirst.dragHandle', 'Drag to reorder')}
        title={t('chaptersFirst.dragHandle', 'Drag to reorder')}
      >
        ⋮⋮
      </button>
      <div className="flex flex-col lg:hidden -my-1">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={!canMoveUp}
          className="px-1.5 text-gray-400 disabled:opacity-30 hover:text-indigo-600 leading-none text-[10px] h-4"
          aria-label={t('chaptersFirst.moveUp', 'Move up')}
        >
          ▲
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={!canMoveDown}
          className="px-1.5 text-gray-400 disabled:opacity-30 hover:text-indigo-600 leading-none text-[10px] h-4"
          aria-label={t('chaptersFirst.moveDown', 'Move down')}
        >
          ▼
        </button>
      </div>
      <button
        type="button"
        onClick={() => onFocusScene(scene.id)}
        className={`flex-1 min-w-0 text-left text-sm px-2 py-1 rounded transition flex items-center justify-between gap-2 ${
          isSelected
            ? 'bg-indigo-100 text-indigo-900 dark:bg-indigo-900/40 dark:text-indigo-100'
            : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
        }`}
      >
        <span className="flex items-center gap-2 min-w-0">
          <HealthDot health={scene.health} />
          <span className="truncate">{scene.title}</span>
        </span>
        <span className="flex gap-1 shrink-0">
          {scene.locked && (
            <span title={t('chaptersFirst.locked', 'Locked')} aria-label="locked">
              🔒
            </span>
          )}
          {scene.previewStale && !scene.locked && (
            <span title={t('chaptersFirst.stale', 'Out of date')} aria-label="stale">
              ⚠️
            </span>
          )}
        </span>
      </button>
      <button
        type="button"
        onClick={() => onNavigate(`/books/${bookId}/scenes/${scene.id}`)}
        className="text-xs text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-300 px-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 focus:opacity-100 transition-opacity"
        aria-label={t('chaptersFirst.openSceneFocus', 'Open scene')}
        title={t('chaptersFirst.openSceneFocus', 'Open scene')}
      >
        ↗
      </button>
    </li>
  );
}

interface ChapterTitleEditableProps {
  chapter: Chapter;
  isEditing: boolean;
  onStartEditing: () => void;
  onStopEditing: (nextTitle: string | null) => void;
}

function ChapterTitleEditable({
  chapter,
  isEditing,
  onStartEditing,
  onStopEditing,
}: ChapterTitleEditableProps) {
  const { t } = useTranslation();

  if (isEditing) {
    return <TitleEditInput initialValue={chapter.title} onCommit={onStopEditing} />;
  }
  return (
    <h2
      className="text-2xl font-bold truncate cursor-text rounded-md -mx-1 px-1 hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
      onClick={onStartEditing}
      title={t('chaptersFirst.renameChapterHint', 'Click to rename')}
    >
      {chapter.title}
    </h2>
  );
}

interface TitleEditInputProps {
  initialValue: string;
  onCommit: (nextTitle: string | null) => void;
}

function TitleEditInput({ initialValue, onCommit }: TitleEditInputProps) {
  const [draft, setDraft] = useState(initialValue);

  const commit = () => onCommit(draft.trim() || null);

  return (
    <Input
      value={draft}
      autoFocus
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onCommit(null);
        }
      }}
    />
  );
}

interface ChapterActionsMenuProps {
  chapter: Chapter;
  index: number;
  total: number;
  isSuggesting: boolean;
  onRename: () => void;
  onAddSceneEnd: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onSuggestScenes: () => void;
  onReshape: () => void;
  onDelete: () => void;
}

function ChapterActionsMenu({
  index,
  total,
  isSuggesting,
  onRename,
  onAddSceneEnd,
  onMoveUp,
  onMoveDown,
  onSuggestScenes,
  onReshape,
  onDelete,
}: ChapterActionsMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        aria-label={t('common.options', 'Options')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center justify-center w-9 h-9 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        {isSuggesting ? (
          <Spinner size="sm" />
        ) : (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
            <circle cx="10" cy="4" r="1.7" />
            <circle cx="10" cy="10" r="1.7" />
            <circle cx="10" cy="16" r="1.7" />
          </svg>
        )}
      </button>
      <DropdownMenu isOpen={open} onClose={() => setOpen(false)} position="right" width="md">
        <DropdownMenuItem
          onClick={() => {
            setOpen(false);
            onRename();
          }}
        >
          {t('common.rename', 'Rename')}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            setOpen(false);
            onAddSceneEnd();
          }}
        >
          {t('chaptersFirst.addSceneBelow', 'Add scene')}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            setOpen(false);
            onSuggestScenes();
          }}
          disabled={isSuggesting}
        >
          {t('chaptersFirst.suggestScenesWithAi', 'Suggest scenes with AI')}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            setOpen(false);
            onMoveUp();
          }}
          disabled={index === 0}
        >
          {t('common.moveUp', 'Move up')}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            setOpen(false);
            onMoveDown();
          }}
          disabled={index === total - 1}
        >
          {t('common.moveDown', 'Move down')}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            setOpen(false);
            onReshape();
          }}
        >
          {t('chaptersFirst.reshapeScenes', 'Reshape scenes (AI flow)…')}
        </DropdownMenuItem>
        <DropdownMenuItem
          variant="danger"
          onClick={() => {
            setOpen(false);
            onDelete();
          }}
        >
          {t('chaptersFirst.deleteChapter', 'Delete chapter')}
        </DropdownMenuItem>
      </DropdownMenu>
    </div>
  );
}

function InsertSceneHairline({ label, onInsert }: { label: string; onInsert: () => void }) {
  return (
    <div className="group relative h-3 -my-1.5">
      <button
        type="button"
        onClick={onInsert}
        aria-label={label}
        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
      >
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-xs font-medium">
          + {label}
        </span>
      </button>
    </div>
  );
}
